import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../auth/AuthProvider';
import { deleteArtifact } from './client';
import { keys } from './keys';

/**
 * Hard-delete an artifact from a workstream (Drop 6 / S10) — the mutation behind
 * the Files rail's Remove confirm dialog. `mutate(artifactId)` calls
 * `DELETE /workstreams/{id}/artifacts/{artifactId}`; on settle it invalidates
 * `keys.workstream.artifacts(workstreamId)` so the refetched list drops the
 * removed card (the list is read in one place, so per frontend-architecture R7
 * this is the pending-UI + invalidate path, not an optimistic cache edit).
 *
 * Consumers drive the double-submit guard off `isPending` (disable the Delete
 * control) and surface `isError` in the dialog. Errors also route to the shared
 * `MutationCache.onError` sink (reportError) via the QueryClient config.
 */
export function useDeleteArtifact(workstreamId: string) {
  const { getAccessToken } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: keys.workstream.deleteArtifact(workstreamId),
    mutationFn: (artifactId: string) =>
      deleteArtifact(workstreamId, artifactId, { getAccessToken }),
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: keys.workstream.artifacts(workstreamId),
      });
    },
  });
}
